import React, { useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { Menu, X, ChevronDown } from "lucide-react";
import { SITE } from "@/lib/site";

const SERVICES_MENU = [
  { to: "/construction-services", label: "All Construction Services" },
  { to: "/roofing", label: "Roofing" },
  { to: "/remodeling", label: "Remodeling" },
  { to: "/services/interior-feature-upgrades", label: "Interior Feature Upgrades" },
  { to: "/services/outdoor-living", label: "Outdoor Living" },
  { to: "/services/home-additions", label: "Home Additions" },
  { to: "/services/adu-construction", label: "ADU Construction" },
  { to: "/services/handyman-punch-list", label: "Repairs & Punch List" },
  { to: "/services/investor-property-repairs", label: "Investor Repairs" },
  { to: "/popular-austin-services", label: "Popular Austin Services" },
];

const MORE_MENU = [
  { to: "/service-areas", label: "Service Areas" },
  { to: "/about", label: "About" },
  { to: "/portfolio", label: "Portfolio" },
  { to: "/careers", label: "Careers" },
  { to: "/testimonials", label: "Testimonials" },
  { to: "/blog", label: "Resources / Blog" },
  { to: "/subcontractor-partnerships", label: "Partner With Us" },
];

export default function Navbar() {
  const [open, setOpen] = useState(false);
  const [servicesOpen, setServicesOpen] = useState(false);
  const [moreOpen, setMoreOpen] = useState(false);
  const location = useLocation();

  React.useEffect(() => {
    setOpen(false);
    setServicesOpen(false);
    setMoreOpen(false);
  }, [location.pathname]);

  const navLink = ({ isActive }) =>
    `px-2.5 py-2 text-[12px] tracking-widest uppercase font-semibold transition whitespace-nowrap ${isActive ? "text-br-bronze-2" : "text-white hover:text-br-bronze-2"}`;

  return (
    <header className="sticky top-0 z-50 bg-br-black border-b border-br-line">
      <div className="container-br flex items-center justify-between gap-4 py-0">
        <Link to="/" className="flex items-center text-white shrink-0 overflow-hidden h-28 sm:h-32 lg:h-36" data-testid="navbar-logo" aria-label="Black Ridge Construction home">
          <img src={SITE.logo} alt="Black Ridge Construction" className="h-56 sm:h-72 lg:h-80 w-auto max-w-none" />
        </Link>
        <nav className="hidden lg:flex items-center gap-1" data-testid="navbar-links">
          <NavLink to="/" end className={navLink}>Home</NavLink>
          <NavLink to="/maintenance-plans" className={navLink}>Maintenance Plans</NavLink>
          <div className="relative" onMouseEnter={() => setServicesOpen(true)} onMouseLeave={() => setServicesOpen(false)}>
            <button className="flex items-center gap-1 px-2.5 py-2 text-[12px] tracking-widest uppercase font-semibold text-white hover:text-br-bronze-2 transition whitespace-nowrap">
              Services <ChevronDown size={14} />
            </button>
            {servicesOpen && (
              <div className="absolute left-0 top-full pt-2 w-72">
                <div className="bg-br-charcoal border border-br-line rounded shadow-xl py-2">
                  {SERVICES_MENU.map((s) => (
                    <Link key={s.to} to={s.to} className="block px-4 py-2 text-[13px] text-gray-200 hover:text-br-bronze-2 hover:bg-br-charcoal-2">
                      {s.label}
                    </Link>
                  ))}
                </div>
              </div>
            )}
          </div>
          <NavLink to="/commercial" className={navLink}>Commercial</NavLink>
          <NavLink to="/contact" className={navLink}>Contact</NavLink>
          <div className="relative" onMouseEnter={() => setMoreOpen(true)} onMouseLeave={() => setMoreOpen(false)}>
            <button className="flex items-center gap-1 px-2.5 py-2 text-[12px] tracking-widest uppercase font-semibold text-white hover:text-br-bronze-2 transition whitespace-nowrap">
              More <ChevronDown size={14} />
            </button>
            {moreOpen && (
              <div className="absolute right-0 top-full pt-2 w-56">
                <div className="bg-br-charcoal border border-br-line rounded shadow-xl py-2">
                  {MORE_MENU.map((m) => (
                    <Link key={m.to} to={m.to} className="block px-4 py-2 text-[13px] text-gray-200 hover:text-br-bronze-2 hover:bg-br-charcoal-2">
                      {m.label}
                    </Link>
                  ))}
                </div>
              </div>
            )}
          </div>
        </nav>
        <div className="hidden lg:flex items-center gap-3 shrink-0">
          <Link to="/contact" className="br-btn-primary text-[12px] py-2.5 px-4 whitespace-nowrap" data-testid="navbar-cta">Get a Quote</Link>
        </div>
        <button className="lg:hidden text-white" onClick={() => setOpen(!open)} aria-label="Menu" data-testid="mobile-menu-btn">
          {open ? <X /> : <Menu />}
        </button>
      </div>
      {open && (
        <div className="lg:hidden bg-br-charcoal border-t border-br-line" data-testid="mobile-menu">
          <div className="px-4 py-4 space-y-1">
            {[
              ["/", "Home"],
              ["/maintenance-plans", "Maintenance Plans"],
              ["/construction-services", "Construction Services"],
              ["/roofing", "Roofing"],
              ["/remodeling", "Remodeling"],
              ["/services/interior-feature-upgrades", "Interior Feature Upgrades"],
              ["/popular-austin-services", "Popular Austin Services"],
              ["/commercial", "Commercial"],
              ["/service-areas", "Service Areas"],
              ["/portfolio", "Portfolio"],
              ["/careers", "Careers"],
              ["/testimonials", "Testimonials"],
              ["/about", "About"],
              ["/blog", "Resources"],
              ["/contact", "Contact"],
              ["/subcontractor-partnerships", "Partner With Us"],
            ].map(([to, label]) => (
              <Link key={to} to={to} className="block px-3 py-3 text-sm text-white border-b border-br-line hover:text-br-bronze-2">{label}</Link>
            ))}
            <a href={`tel:${SITE.phoneE164}`} className="block px-3 py-3 text-br-bronze-2 font-semibold">Call {SITE.phone}</a>
          </div>
        </div>
      )}
    </header>
  );
}
